import '@expo/metro-runtime';
import type { NavigationContainerRef } from '@react-navigation/native';
import { NavigationContainer } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { setNotificationHandler } from 'expo-notifications';
import { maybeCompleteAuthSession } from 'expo-web-browser';
import { StrictMode, useEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { PaperProvider } from 'react-native-paper';
import { de, enGB, registerTranslation } from 'react-native-paper-dates';
import {
  configureReanimatedLogger,
  ReanimatedLogLevel,
  ReducedMotionConfig,
  ReduceMotion
} from 'react-native-reanimated';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { queryClient, persistOptions } from '@/configs/query';
import { APP_NAME } from '@/constants/app';
import { WEB_MAX_WIDTH } from '@/constants/web';
import { AppStateProvider } from '@/features/authorization/provider/AppStateProvider';
import { AuthProvider } from '@/features/authorization/provider/AuthProvider';
import { FeatureProvider } from '@/features/feature-management/provider/FeatureProvider';
import { FlagProvider } from '@/features/flag/providers/FlagProvider';
import { NotificationConfiguration } from '@/features/notifications/components/NotificationConfiguration';
import { registerForPushNotificationsAsync } from '@/features/notifications/utils/push';
import { PopupProvider } from '@/features/popup/provider/PopupProvider';
import { useAppSettingStore } from '@/features/profile-setting/store/useAppSettingStore';
import { TutorialProvider } from '@/features/tutorial/providers/TutorialProvider';
import { VerificationProvider } from '@/features/verification/providers/VerificationProvider';
import { Locale } from '@/localization/types/localizationTypes';
import { updateLocalizationInstance } from '@/localization/utils/localization';
import { linking } from '@/navigation/configs/linking';
import { AppRouting } from '@/navigation/router/AppRouting';
import type { AppParam } from '@/navigation/types/navigation';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useFontLoader } from '@/styles/hooks/useFontLoader';
import { useGetAppTheme } from '@/styles/hooks/useGetAppTheme';
import { navigationIntegration } from '@/tracking';
import '@/localization';

maybeCompleteAuthSession();

// Deliberate overrides (e.g. ReducedMotionConfig below) are intentional app
// configuration, not mistakes — keep Reanimated's strict-mode advisories
// (both the "overwritten" and the device "reduced motion enabled" nag) quiet.
configureReanimatedLogger({
  level: ReanimatedLogLevel.error,
  strict: false
});

// #region Notifications
setNotificationHandler({
  // oxlint-disable-next-line @typescript-eslint/require-await
  handleNotification: async (notification) => ({
    shouldShowList: !isEmptyOrNull(notification?.request?.content?.body),
    shouldShowBanner: !isEmptyOrNull(notification?.request?.content?.body),
    shouldPlaySound: false,
    shouldSetBadge: false
  })
});

// #endregion Notifications

const App = () => {
  const navigationContainerRef = useRef<NavigationContainerRef<AppParam>>(null);
  useFontLoader();

  registerTranslation(Locale.EN, enGB);
  registerTranslation(Locale.DE, de);

  const selectedLocale = useAppSettingStore((state) => state.selectedLocale);

  // Set localization instance based on stored locale
  useEffect(() => {
    updateLocalizationInstance(selectedLocale);
  }, [selectedLocale]);

  const theme = useGetAppTheme();

  useEffect(() => void registerForPushNotificationsAsync());

  return (
    <StrictMode>
      <ReducedMotionConfig mode={ReduceMotion.Never} />
      <KeyboardProvider>
        <SafeAreaProvider>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={persistOptions}
          >
            <PaperProvider theme={theme}>
              <GestureHandlerRootView style={{ flex: 1 }}>
                <AuthProvider>
                  <AppStateProvider>
                    {/*
                     * The max-width constraint wraps ONLY the navigation
                     * content, not the Portal host (which lives in
                     * PaperProvider). Portals (menus, modals) therefore render
                     * in the same viewport coordinate frame that
                     * measureInWindow returns, so anchored overlays need no
                     * coordinate-offset correction.
                     */}
                    <View
                      style={Platform.select({
                        web: {
                          flex: 1,
                          width: '100%',
                          maxWidth: WEB_MAX_WIDTH,
                          marginHorizontal: 'auto',
                          boxShadow: '0 0 50px rgba(0,0,0,0.25)',
                          backgroundColor: 'transparent',
                          // Establish a positioning context so screens using
                          // absoluteFill (e.g. modal presentations) are
                          // contained within this box instead of escaping to
                          // the full viewport.
                          position: 'relative'
                        },
                        default: { flex: 1 }
                      })}
                    >
                      <NavigationContainer
                        theme={theme}
                        ref={navigationContainerRef}
                        linking={linking}
                        documentTitle={{ formatter: () => APP_NAME }}
                        onReady={() => {
                          navigationIntegration.registerNavigationContainer(
                            navigationContainerRef
                          );
                        }}
                      >
                        <FeatureProvider>
                          <PopupProvider>
                            <NotificationConfiguration />
                            <VerificationProvider>
                              <FlagProvider>
                                <TutorialProvider>
                                  <SystemBars style="dark" />
                                  <AppRouting />
                                </TutorialProvider>
                              </FlagProvider>
                            </VerificationProvider>
                          </PopupProvider>
                        </FeatureProvider>
                      </NavigationContainer>
                    </View>
                  </AppStateProvider>
                </AuthProvider>
              </GestureHandlerRootView>
            </PaperProvider>
          </PersistQueryClientProvider>
        </SafeAreaProvider>
      </KeyboardProvider>
    </StrictMode>
  );
};

const SentryApp = Sentry.wrap(App);
export default SentryApp;
